import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import {CLStore} from 'common/src/redux/store';
import {z} from 'zod';
import {CampaignSetupAgentToolName} from '../../../../campaignSetupAgent.enums';
import getStrategies, {FeedType} from '../../../../utils/getStrategies';
import productSelectionStrategiesApi from 'common/src/modules/productSelectionStrategies/productSelectionStrategies.api';
import {IdPrefix} from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/idHelpers.enums';
import toId from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/utils/toId';

const inputSchema = z.object({
  feedType: z.nativeEnum(FeedType).describe('The feed type the user is working with'),
});
const STRATEGY_ID_NOTE =
  'Note: Strategy IDs are internal tool parameters and should not be exposed to end users.';

type SelectionStrategy = {
  id: number;
  name: string;
  purpose?: string;
};

type FeedConfig = {
  title: string;
  prefix: IdPrefix;
  getStrategies: () => Promise<SelectionStrategy[]>;
};

function listSelectionStrategiesTool(store: CLStore) {
  async function execute({
    input: {feedType},
  }: ToolExecuteParams<typeof inputSchema>): Promise<ToolResult> {
    try {
      const campaign = store.getState().campaignPage.campaignTree.campaign;

      if (!campaign) {
        return toolError('Campaign not found');
      }

      const campaignType = campaign.type;
      const feedConfig: Record<FeedType, FeedConfig> = {
        [FeedType.Product]: {
          title: 'Product Strategies',
          prefix: IdPrefix.ProductStrategy,
          getStrategies: async () => {
            const productStrategies = await productSelectionStrategiesApi.getProductStrategies(
              campaign.id,
            );

            return productStrategies.map((strategy) => ({
              id: strategy.strategy,
              name: strategy.uiName,
              purpose: strategy.purposeForAI,
            }));
          },
        },
        [FeedType.Location]: {
          title: 'Location Strategies',
          prefix: IdPrefix.LocationStrategy,
          getStrategies: async () =>
            getStrategies(FeedType.Location, campaignType).map((strategy) => ({
              id: strategy.strategyId,
              name: strategy.name,
            })),
        },
        [FeedType.Content]: {
          title: 'Content Strategies',
          prefix: IdPrefix.ContentStrategy,
          getStrategies: async () =>
            getStrategies(FeedType.Content, campaignType).map((strategy) => ({
              id: strategy.strategyId,
              name: strategy.name,
            })),
        },
      };

      const {title, prefix, getStrategies: getStrategiesForFeed} = feedConfig[feedType];
      const strategies = await getStrategiesForFeed();
      const strategiesBody = strategies
        .map((strategy) => {
          const baseLines = [
            `ID: ${toId(prefix, strategy.id)}`,
            `Name: ${strategy.name}`,
          ];

          return strategy.purpose
            ? `${baseLines.join('\n')}\nPurpose: ${strategy.purpose}`
            : baseLines.join('\n');
        })
        .join('\n\n');

      return toolResult(`= ${title} =\n${strategiesBody}\n\n${STRATEGY_ID_NOTE}`);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';

      return toolError(`Error retrieving strategies: ${message}`);
    }
  }

  return tool({
    name: CampaignSetupAgentToolName.ListSelectionStrategies,
    displayName: 'List available strategies',
    description:
      'Lists all available selection strategies for a given feed type. Call this tool first to discover which strategies exist and choose the right one.',
    execute,
    inputSchema,
  });
}

export default listSelectionStrategiesTool;
